import {
    Component, Input, forwardRef, ChangeDetectorRef, ChangeDetectionStrategy,
    OnInit, OnDestroy, ViewChild, Output, EventEmitter, HostListener
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ControlValueAccessor, FormControl } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, finalize } from 'rxjs/operators';
import { IAutocompleteOptions, AutocompleteOptions } from '../../model/autocomplete.model';
import { get } from '../../../core/core';
import { valueType } from '../../model/lists.model';

const AUTOCOMPLETE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi: true
};

const VALIDATOR_ACCESSOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi: true,
}

@Component({
    selector: 'aria-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [AUTOCOMPLETE_ACCESSOR, VALIDATOR_ACCESSOR],
})
export class AutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

    @Input() placeholder: string;
    @Input() showLabel: boolean = false;
    @Input() options: IAutocompleteOptions;
    // initialize label field
    @Input() label: string;
    @Input() retrieveCall: Function;
    @Input() autocompleteList: Array<any>;
    @Input() debounceTime: number;
    @Input() disabled: boolean;
    @Input() loadOnFocus: boolean;
    @Input() name: string;
    @Input() index: number = 1;
    @Output() select: EventEmitter<any> = new EventEmitter();
    @Input() ariaInvalid: boolean = false;

    optionsList: Array<any>;
    openAutocomplete: boolean = false;
    selectedOption: any = {};
    isOptionSelected: boolean = false;
    inLoading: boolean = false;

    @ViewChild('autocompleteInput', { static: true }) autocompleteInput: any;

    private _value: valueType;
    private _debounce$: Subject<string>;
    private _outputKey: string;
    private _labelKey: string;
    private _styleOnlyAuto: boolean;
    private _optionsFocusIndex: number = -1;

    constructor(private cdr: ChangeDetectorRef) {
        this.optionsList = [];
        this._debounce$ = new Subject<string>();
    }

    ngOnInit() {
        if (this.options) {
            this.options = new AutocompleteOptions(this.options);
            this._outputKey = this.options.outputKey;
            this._labelKey = this.options.labelKey;
            this._styleOnlyAuto = this.options.styleOnlyAuto;
        }

        if (this.autocompleteList && Array.isArray(this.autocompleteList)) {
            this.optionsList = this.autocompleteList;
        } else {
            this.autocompleteList = null;
        }

        this._debounce$.asObservable().pipe(
            debounceTime(this.debounceTime || 300),
            distinctUntilChanged(),
            switchMap((searchValue: string) => this.getOptionsList(searchValue))
        ).subscribe((options: Array<any>) => { this.cdr.markForCheck(), this.optionsList = options });
    }

    ngOnDestroy() {
        this._debounce$.unsubscribe();
    }

    //SETTERS / GETTERS

    set debounce(searchedValue: string) {
        this._debounce$.next(searchedValue);
    }

    get value(): any {
        return this._value;
    };

    set value(v: any) {
        if (v !== this._value) {
            this._value = v;

            this.cdr.markForCheck();
        }
    }

    @HostListener('keydown', ['$event']) onKeyDown(e) {

        if (!this.openAutocomplete || (this.openAutocomplete && !this.optionsList.length)) return;
        if (e.key == 'Tab') {
            this.openAutocomplete = false;
            return;
        }
        if (!/ArrowUp|ArrowDown/.test(e.key)) return;

        const keyName = e.key;

        if (this._optionsFocusIndex == -1) {
            this._optionsFocusIndex = 0;
        } else if (keyName == 'ArrowUp') {
            this._optionsFocusIndex--;
        } else if (keyName == 'ArrowDown') {
            if (this._optionsFocusIndex != this.optionsList.length - 1)
                this._optionsFocusIndex++;
        }

        if (this._optionsFocusIndex == -1) {
            this.autocompleteInput.nativeElement.focus();
        } else {
            document.getElementById(`autoOpt-${this._optionsFocusIndex}`).focus();
        }
    }

    /**
     * Set value is used to setup output ngModel value
     * @param value string or number to change
     */
    setValue(value: valueType) {
        this.value = value;
        this.onChange(value)
    }

    /**
     * get value from autocomplete option by options outputKey
     * @param option option from autocomplete
     */
    getOptionValue(option: any) {
        return this._outputKey ? get(option, this._outputKey) : option;
    }

    /**
     * get label from autocomplete option by options labelKey
     * @param option option from autocomplete
     */
    getOptionLabel(option: any) {
        if (typeof option !== 'string') {
            return this._labelKey ? get(option, this._labelKey) : this.getOptionValue(option);
        } else {
            return option;
        }
    }

    /**
     * get Observable to retrieve autocomplete options list
     * @param value string or number to change
     */
    getOptionsList(value: valueType): Observable<any> {
        if (typeof this.retrieveCall == 'function') {
            this.inLoading = true;
            return this.retrieveCall(value).pipe(finalize(() => this.inLoading = false));
        } else {
            return of(this.autocompleteList || []);
        }
    }

    /**
     * On model change is used to reset output value or to reset value from options list 
     * @param value  string or number inputed from user to change
     */
    onModelChange(value) {
        const option = this.optionsList.find((option) => this.getOptionLabel(option) === value);
        this.label = value;
        this.setValue(!!option ? this.getOptionValue(option) : null);

        this.selectedOption = this._styleOnlyAuto ? {} : option || {};

        this.autocompleteInput.invalid = !option;

        this.emitSelection(option);


        if (typeof this.retrieveCall == 'function') {
            this.debounce = value;
        }
        this.cdr.markForCheck();
    }

    /**
     * Select option from autocomplete list
     * @param option abject or string to select
     */
    selectOption(option: any) {
        this.label = this.getOptionLabel(option);
        this.setValue(this.getOptionValue(option));
        this.selectedOption = this._styleOnlyAuto ? {} : option;
        this.openAutocomplete = false;

        this.emitSelection(option);

        this.cdr.markForCheck();
    }

    /**
     * Emit selection in select envent
     * @param option current option selected
     */
    emitSelection(option: any) {
        if (this.isOptionSelected && !option) {
            this.isOptionSelected = false;
            this.select.emit({
                label: null,
                value: null,
                option: {}
            });
        }
        if (option) {
            this.isOptionSelected = true;
            this.select.emit({
                label: this.label,
                value: this.value,
                option: option
            });
        }
    }

    /**
     * On input focus open autocomplete or do the first call to retrieve data and than open autocomplete
     * @param value current value of input itself
     */
    onFocus(value) {
        this.openAutocomplete = true;
        this._optionsFocusIndex = -1;

        if (!this.optionsList.length && this.loadOnFocus) {
            this.inLoading = true;
            this.getOptionsList(this.label || value)
                .pipe(finalize(() => this.inLoading = false))
                .subscribe((options: Array<any>) => {
                    this.cdr.markForCheck();
                    this.optionsList = options
                });
        }
    }

    /**
     * On click outside autocomplete div or input close autocomplete
     * @param event click event
     */
    onBlur(event) {

        if (!event.relatedTarget || !/autoOpt-\d*/.test(event.relatedTarget.id)) {
            this.openAutocomplete = false;
        } else if (/autocomplete/.test(event.relatedTarget.id)) {
            this.openAutocomplete = false;
            this.autocompleteInput.nativeElement.focus();
        }
    }


    // REQURED METHODS FOR NGMODEL DESIGN PATTERN

    public validate(c: FormControl) {
        let errors = { ...c.errors }, hasError = false;

        if (this.label && !this.value) {
            hasError = true;
            errors['noSelectionError'] = { valid: false }
        }

        if (hasError || c.invalid) {
            return errors;
        } else {
            return null;
        }
    }

    /**
     * Reuired from ngModel design pattern to work
     * @param value initial value
     */
    writeValue(value): void {
        if (String(value) !== String(this._value)) {
            this.label = value || typeof value == 'number' ? this.label || value : '';
            this.setValue(value);
            this.cdr.markForCheck();
        }
    }

    onChange = (_) => { };
    onTouched = () => { };

    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

}